// CRM 2.0 · M12-F4: the back camera, reading QR codes continuously. Permission is asked once by the browser; the
// camera needs a secure (https) page. While `paused` the picture stays but nothing is read (a sheet is open).
// A sharp picture matters more than speed for an 18 mm printed QR:
//   - a large picture (up to 1920 × 1080), continuous focus and a little zoom where the phone allows them
//   - the phone's main back lens (phones with 2–3 back lenses often give the wide one first, which cannot focus
//     close); "Switch camera" moves to the next back lens
//   - the phone's own QR reader where there is one (BarcodeDetector, Android Chrome), else jsqr on the middle of
//     the picture at full size (no shrinking)
import React, { useEffect, useRef, useState } from "react";
import jsQR from "jsqr";
import cls from "./Crm2Verify.module.css";

const READ_EVERY_MS = 150;
const SAME_CODE_AGAIN_MS = 2500;
const BACK = /back|rear|environment|trás|arrière/i;
const WIDE = /wide|ultra|macro|tele|depth/i;

// the back lenses, main one first: by label "camera2 0" < "camera2 2"; a lens named wide / ultra / macro last
function backCameras(devices) {
  const cams = devices.filter((d) => d.kind === "videoinput");
  const back = cams.filter((d) => BACK.test(d.label));
  const list = back.length ? back : cams;
  return [...list].sort((a, b) => (WIDE.test(a.label) ? 1 : 0) - (WIDE.test(b.label) ? 1 : 0) || a.label.localeCompare(b.label, undefined, { numeric: true }));
}

async function sharpen(track) {
  const caps = track.getCapabilities ? track.getCapabilities() : {};
  const advanced = [];
  if (caps.focusMode && caps.focusMode.includes("continuous")) advanced.push({ focusMode: "continuous" });
  // a little zoom lets the phone stay far enough from the paper to focus
  if (caps.zoom && caps.zoom.max >= 1.5) advanced.push({ zoom: Math.min(2, caps.zoom.max) });
  if (advanced.length) await track.applyConstraints({ advanced }).catch(() => {});
}

function QrCamera({ paused, onRead, hint }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const pausedRef = useRef(paused);
  const readRef = useRef(onRead);
  const last = useRef({ text: "", at: 0 });
  const [problem, setProblem] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [camera, setCamera] = useState(null); // deviceId; null = let the browser pick the back camera first

  pausedRef.current = paused;
  readRef.current = onRead;

  useEffect(() => {
    let stream = null;
    let timer = null;
    let stopped = false;
    if (!window.isSecureContext || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setProblem("The camera needs a secure (https) connection to the Sales App.");
      return undefined;
    }
    const size = { width: { ideal: 1920 }, height: { ideal: 1080 } };
    const video = camera ? { deviceId: { exact: camera }, ...size } : { facingMode: { ideal: "environment" }, ...size };
    const detector = "BarcodeDetector" in window ? new window.BarcodeDetector({ formats: ["qr_code"] }) : null;

    const found = (text) => {
      const now = Date.now();
      if (text && !(text === last.current.text && now - last.current.at < SAME_CODE_AGAIN_MS)) {
        last.current = { text, at: now };
        readRef.current(text);
      }
    };

    // the middle of the picture (where the frame is) at full size, for jsqr
    const readWithJsQr = (v, c) => {
      const side = Math.round(Math.min(v.videoWidth, v.videoHeight) * 0.8);
      const out = Math.min(side, 1000);
      c.width = out;
      c.height = out;
      const ctx = c.getContext("2d", { willReadFrequently: true });
      ctx.drawImage(v, (v.videoWidth - side) / 2, (v.videoHeight - side) / 2, side, side, 0, 0, out, out);
      const code = jsQR(ctx.getImageData(0, 0, out, out).data, out, out, { inversionAttempts: "dontInvert" });
      return code && code.data;
    };

    navigator.mediaDevices.getUserMedia({ video, audio: false })
      .then(async (s) => {
        if (stopped) { s.getTracks().forEach((t) => t.stop()); return; }
        stream = s;
        const track = s.getVideoTracks()[0];
        // labels are known once permission is given: move to the main back lens if the browser gave another one
        const list = backCameras(await navigator.mediaDevices.enumerateDevices());
        if (!stopped) setCameras(list);
        const current = track.getSettings ? track.getSettings().deviceId : null;
        if (!camera && list.length > 1 && current && list[0].deviceId && current !== list[0].deviceId) {
          setCamera(list[0].deviceId); // reopens on the main lens (this effect runs again)
          return;
        }
        await sharpen(track);
        const v = videoRef.current;
        v.srcObject = s;
        v.setAttribute("playsinline", "true");
        v.play().catch(() => {});
        const tick = async () => {
          if (stopped) return;
          const el = videoRef.current;
          if (!pausedRef.current && el && el.readyState >= 2 && el.videoWidth) {
            try {
              if (detector) {
                const codes = await detector.detect(el);
                found(codes && codes[0] && codes[0].rawValue);
              } else {
                found(readWithJsQr(el, canvasRef.current));
              }
            } catch (e) {
              // a frame that could not be read: the next one is tried
            }
          }
          timer = setTimeout(tick, READ_EVERY_MS);
        };
        tick();
      })
      .catch((e) => setProblem(e && e.name === "NotAllowedError"
        ? "Allow the camera for this site to scan the QR codes."
        : "The camera could not be started on this device."));
    return () => {
      stopped = true;
      clearTimeout(timer);
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
  }, [camera]);

  // tapping the picture asks the camera to focus again
  const refocus = () => {
    const s = videoRef.current && videoRef.current.srcObject;
    const track = s && s.getVideoTracks()[0];
    if (track) sharpen(track);
  };

  const next = () => {
    if (cameras.length < 2) return;
    const i = cameras.findIndex((c) => c.deviceId === camera);
    setCamera(cameras[(i + 1) % cameras.length].deviceId);
  };

  return (
    <div className={cls.cam}>
      <video ref={videoRef} className={cls.video} muted playsInline onClick={refocus} />
      <canvas ref={canvasRef} hidden />
      <div className={cls.frame} aria-hidden="true" />
      {hint && !problem && <p className={cls.hint}>{hint}</p>}
      {cameras.length > 1 && <button className={`${cls.chipBtn} ${cls.switchCam}`} onClick={next}>Switch camera</button>}
      {problem && <div className={cls.camProblem}>{problem}</div>}
    </div>
  );
}

export default QrCamera;
